import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import ShelyUrls from '../../components/ShelyUrls';
import GateConfigMenu from '../../components/GateConfigMenu';
import GateTimeline from '../../components/GateTimeline';
import GateControlPanel from '../../components/GateControlPanel';
import GateCommandsLog from '../../components/GateCommandsLog';
import GateTempUnlockSection from '../../components/GateTempUnlockSection';
import FaultHistoryBadge from '../../components/FaultHistoryBadge';
import FaultAcknowledgeButton from '../../components/FaultAcknowledgeButton';
import HermeticLockIndicator from '../../components/HermeticLockIndicator';
import HermeticLockDebugPanel from '../../components/HermeticLockDebugPanel';
import MqttStatusIndicator from '../../components/MqttStatusIndicator';
import { IconFile } from '../../components/Icons';

function GateDetail() {
  const { id } = useParams();
  const [gate, setGate] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [settings, setSettings] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [commandsRefreshKey, setCommandsRefreshKey] = useState(0);

  const loadGate = () => {
    axios.get(`/api/admin/gates/${id}`).then(res => setGate(res.data));
  };

  const loadSettings = () => {
    axios.get(`/api/admin/gates/${id}/settings`).then(res => setSettings(res.data));
  };

  useEffect(() => {
    loadGate();
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const revertToGroup = () => {
    if (!window.confirm('להחזיר את השער להגדרות הקבוצה? ההגדרות הפרטיות שלו יימחקו.')) return;
    Promise.all([
      axios.delete(`/api/admin/gates/${id}/settings`),
      axios.delete(`/api/admin/gates/${id}/schedule`),
    ]).then(() => { loadGate(); loadSettings(); });
  };

  const inheritedFromGroup = settings?.source === 'group';
  const hasOwnOverride = settings?.source === 'gate';

  if (!gate) return <div className="loading">טוען...</div>;

  const cyclePercent = gate.cycle_limit ? Math.round((gate.cycle_count / gate.cycle_limit) * 100) : null;

  return (
    <>
      <div className="breadcrumb">
        <Link to="/customers">לקוחות</Link>
        <span>/</span>
        <span>{gate.name}</span>
      </div>

      {gate.group_name && (
        <div style={{
          display: 'inline-block',
          fontSize: '0.78rem',
          color: '#1e40af',
          background: '#eff6ff',
          padding: '0.2rem 0.55rem',
          borderRadius: '4px',
          marginBottom: '0.85rem',
        }}>
          📁 קבוצה: {gate.group_name}
        </div>
      )}

      <div className="stats-row">
        <div className={`stat-card ${gate.is_open ? 'red' : 'green'}`}>
          <div className="number">{gate.is_open ? 'פתוח' : 'סגור'}</div>
          <div className="label">סטטוס נוכחי</div>
        </div>
        <div className="stat-card">
          <div className="number">{gate.cycle_count.toLocaleString()}</div>
          <div className="label">סייקלים</div>
        </div>
        {cyclePercent !== null && (
          <div className={`stat-card ${cyclePercent >= 100 ? 'red' : cyclePercent >= 90 ? 'yellow' : 'green'}`}>
            <div className="number">{cyclePercent}%</div>
            <div className="label">מיעד הטיפול</div>
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem', alignItems: 'center' }}>
        {gate.fault_history && (
          <FaultHistoryBadge faultHistory={gate.fault_history} />
        )}
        <MqttStatusIndicator mqtt={gate.mqtt} />
        <FaultAcknowledgeButton
          gate={gate}
          apiPrefix="/api/admin"
          onResolved={loadGate}
        />
      </div>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem' }}>
        <div className="info-card" style={{ flex: 1 }}>
          <div className="section-header">
            <h3>פרטי השער</h3>
            <button className="btn btn-primary btn-sm" onClick={() => setShowEditModal(true)}>ערוך</button>
          </div>
          <div className="info-row">
            <span className="info-label">דגם</span>
            <span className="info-value">{gate.model || '-'}</span>
          </div>
          <div className="info-row">
            <span className="info-label">רכיב מחובר</span>
            <span className="info-value">{gate.sheli_id || '-'}</span>
          </div>
          <div className="info-row">
            <span className="info-label">סוג חיווט</span>
            <span className="info-value">{wiringTypeLabel(gate.wiring_type)}</span>
          </div>
          <div className="info-row">
            <span className="info-label">גובה הפתח</span>
            <span className="info-value">{gate.opening_height_meters != null ? `${gate.opening_height_meters} מטר` : '-'}</span>
          </div>
          <div className="info-row" title="הבקר של השער סוגר את עצמו אוטומטית בלי שאנחנו שולחים פקודה.">
            <span className="info-label">סגירה אוטומטית בבקר</span>
            <span className="info-value">{gate.has_built_in_auto_close ? 'כן' : 'לא'}</span>
          </div>
          <div className="info-row" title="משווה גובה הוא הרמפה שבין משאית לרצפת המחסן — רלוונטי לרציפי העמסה.">
            <span className="info-label">משווה גובה מתחת לדלת</span>
            <span className="info-value">{gate.is_continuous_with_leveler ? 'כן' : 'לא'}</span>
          </div>
          {gate.is_continuous_with_leveler && (
            <div className="info-row" title="נמדד מחיישן המשווה גובה דרך S2 של הרכיב. עובר באוטומטית דרך debounce של שנייה כדי לבלוע את הריכוז של הריליי במעבר בין מצבים.">
              <span className="info-label">מצב משווה גובה</span>
              <span className="info-value">
                <span style={{
                  display: 'inline-block',
                  padding: '0.1rem 0.55rem',
                  borderRadius: '999px',
                  background: gate.leveler_state === 'working' ? '#fff7ed' : '#f0fdf4',
                  border: `1px solid ${gate.leveler_state === 'working' ? '#fb923c' : '#86efac'}`,
                  color: gate.leveler_state === 'working' ? '#9a3412' : '#166534',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                }}>
                  {gate.leveler_state === 'working' ? 'בעבודה' : 'במנוחה'}
                </span>
                {gate.leveler_state_changed_at && (
                  <span style={{ marginInlineStart: '0.5rem', fontSize: '0.8rem', color: '#6b7280' }}>
                    מאז {new Date(gate.leveler_state_changed_at).toLocaleString('he-IL', { timeZone: 'Asia/Jerusalem' })}
                  </span>
                )}
              </span>
            </div>
          )}
          <div className="info-row" title="הזמן שהמערכת מחכה אחרי פקודה לפני שמותר לשלוח פקודה נוספת. מחושב לפי גובה השער + מהירות הדגם, או ערך עקיפה ידני אם נקבע, או 45 שניות כברירת מחדל.">
            <span className="info-label">זמן תנועה צפוי</span>
            <span className="info-value">
              {gate.effective_movement_timeout_seconds || '—'} ש׳
              {gate.effective_movement_timeout_source && (
                <span style={{ marginInlineStart: '0.4rem', fontSize: '0.78rem', color: '#9ca3af' }}>
                  ({movementTimeoutSourceLabel(gate.effective_movement_timeout_source)})
                </span>
              )}
            </span>
          </div>
          <div className="info-row">
            <span className="info-label">תאריך התקנה</span>
            <span className="info-value">{gate.installed_at || '-'}</span>
          </div>
          <div className="info-row">
            <span className="info-label">טיפול אחרון</span>
            <span className="info-value">{gate.last_maintenance || '-'}</span>
          </div>
          <div className="info-row">
            <span className="info-label">יעד סייקלים</span>
            <span className="info-value">{gate.cycle_limit ? gate.cycle_limit.toLocaleString() : '-'}</span>
          </div>

          {gate.product && (
            <>
              <div className="product-summary-card">
                <div className="product-summary-image">
                  {gate.product.image_url ? (
                    <img src={gate.product.image_url} alt="" />
                  ) : (
                    gate.product.category_icon_closed_url ? (
                      <img src={gate.product.category_icon_closed_url} alt="" />
                    ) : null
                  )}
                </div>
                <div className="product-summary-body">
                  <Link to={`/products/${gate.product.id}`} className="product-summary-name">{gate.product.name}</Link>
                  <div className="product-summary-meta">
                    {gate.product.category_name && <>{gate.product.category_name}</>}
                    {gate.product.model_number && <> · {gate.product.model_number}</>}
                    {gate.product.manufacturer && <> · {gate.product.manufacturer}</>}
                  </div>
                  {gate.product.description && <div className="product-summary-desc">{gate.product.description}</div>}
                </div>
              </div>

              {gate.product.documents && gate.product.documents.length > 0 && (
                <div style={{ marginTop: '0.75rem' }}>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: '0.4rem' }}>מסמכים ופרוטוקולים (פנימי)</div>
                  <div className="doc-list">
                    {gate.product.documents.map(d => (
                      <div key={d.id} className="doc-row">
                        <div className="doc-row-icon"><IconFile size={18} /></div>
                        <div className="doc-row-main">
                          <a href={d.url} target="_blank" rel="noreferrer" className="doc-row-name">
                            {d.name || d.original_filename}
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          <ShelyUrls sheliId={gate.sheli_id} />

          <button
            onClick={() => setConfigOpen(true)}
            disabled={settings == null}
            style={{
              display: 'flex',
              width: '100%',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.75rem 0.85rem',
              border: '1px solid #e5e7eb',
              background: '#fff',
              borderRadius: '6px',
              marginTop: '1rem',
              cursor: settings == null ? 'wait' : 'pointer',
              fontSize: '0.95rem',
              fontWeight: 500,
              textAlign: 'right',
              color: '#1a1a2e',
            }}
          >
            <span>הגדרות שער {inheritedFromGroup && <span style={{ fontSize: '0.72rem', color: '#1e40af', fontWeight: 400, marginRight: '0.4rem' }}>· מהקבוצה</span>}</span>
            <span style={{ color: '#9ca3af' }}>‹</span>
          </button>
          {hasOwnOverride && gate.group_name && (
            <button
              onClick={revertToGroup}
              style={{
                display: 'block',
                width: '100%',
                padding: '0.4rem 0.85rem',
                border: '1px solid #e5e7eb',
                background: '#f9fafb',
                color: '#6b7280',
                borderRadius: '5px',
                fontSize: '0.78rem',
                cursor: 'pointer',
                textAlign: 'right',
                marginTop: '0.4rem',
              }}
            >
              ↩ השב להגדרות הקבוצה
            </button>
          )}
          {settings && (
            <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '0.4rem', display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
              <span>שער פתוח זמן רב: {settings.long_open_alert_enabled ? (settings.long_open_threshold_minutes ? `פעיל (${settings.long_open_threshold_minutes} דק׳)` : 'פעיל') : 'כבוי'}</span>
              <span>תקלה: {settings.fault_alert_enabled ? 'פעיל' : 'כבוי'}</span>
              <span>רכיב לא מחובר: {settings.offline_alert_enabled ? `פעיל (${settings.offline_threshold_minutes} דק׳)` : 'כבוי'}</span>
            </div>
          )}
        </div>
      </div>

      <HermeticLockDebugPanel
        gateId={gate.id}
        wiringType={gate.wiring_type}
      />

      <div className="info-card" style={{ marginBottom: '1.25rem' }}>
        <h3 style={{ marginTop: 0, marginBottom: '0.85rem', fontSize: '0.95rem', fontWeight: 600 }}>שליטה ידנית</h3>
        <HermeticLockIndicator
          wiringType={gate.wiring_type}
          isLocked={!!gate.is_hermetically_locked}
          shouldBeLocked={!!gate.should_be_hermetically_locked}
        />
        <GateControlPanel
          gateId={gate.id}
          isOpen={gate.is_open}
          apiBase="admin"
          isDeviceOnline={gate.mqtt?.status === 'online'}
          onCommandComplete={() => setCommandsRefreshKey(k => k + 1)}
        />
        <GateCommandsLog gateId={gate.id} apiBase="admin" refreshKey={commandsRefreshKey} />
      </div>

      {/* Temp-unlock is only meaningful when hermetic lock is enabled —
          with it off the gate is never auto-locked, so there's nothing
          to release. Mirrors the customer panel. */}
      {settings?.hermetic_lock_enabled && (
        <div className="info-card" style={{ marginBottom: '1.25rem' }}>
          <GateTempUnlockSection gateId={gate.id} apiBase="admin" canManage={true} />
        </div>
      )}

      <div className="info-card" style={{ marginBottom: '1.25rem' }}>
        <h3 style={{ marginTop: 0, marginBottom: '0.85rem', fontSize: '0.95rem', fontWeight: 600 }}>היסטוריית אירועים</h3>
        <GateTimeline gateId={gate.id} apiBase="admin" />
      </div>

      {showEditModal && (
        <EditGateModal
          gate={gate}
          onClose={() => setShowEditModal(false)}
          onSaved={() => { setShowEditModal(false); loadGate(); }}
        />
      )}

      {configOpen && settings && (
        <GateConfigMenu
          gateId={gate.id}
          initialSettings={settings}
          apiBase="admin"
          onClose={() => setConfigOpen(false)}
          onApplied={() => loadSettings()}
        />
      )}
    </>
  );
}

function EditGateModal({ gate, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: gate.name || '',
    product_id: gate.product_id ? String(gate.product_id) : '',
    model: gate.model || '',
    sheli_id: gate.sheli_id || '',
    cycle_limit: gate.cycle_limit || '',
    last_maintenance: gate.last_maintenance || '',
    // Stored as Float in DB; the form edits a decimal string so the
    // operator can type 2.85 etc. without input-type quirks.
    opening_height_meters: gate.opening_height_meters != null
      ? String(gate.opening_height_meters) : '',
    movement_timeout_seconds: gate.movement_timeout_seconds != null
      ? String(gate.movement_timeout_seconds) : '',
    has_built_in_auto_close: !!gate.has_built_in_auto_close,
    is_continuous_with_leveler: !!gate.is_continuous_with_leveler,
  });
  const [products, setProducts] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get('/api/admin/products').then(res => setProducts(res.data));
  }, []);

  const handlePickProduct = (productId) => {
    const product = products.find(p => String(p.id) === String(productId));
    setForm(prev => ({
      ...prev,
      product_id: productId,
      model: product ? (product.model_number || product.name) : prev.model,
      cycle_limit: product && product.default_cycle_limit
        ? String(product.default_cycle_limit)
        : prev.cycle_limit,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await axios.put(`/api/admin/gates/${gate.id}`, {
        name: form.name,
        product_id: form.product_id ? parseInt(form.product_id) : null,
        model: form.model || null,
        sheli_id: form.sheli_id || null,
        cycle_limit: form.cycle_limit ? parseInt(form.cycle_limit) : null,
        last_maintenance: form.last_maintenance || null,
        opening_height_meters: form.opening_height_meters === ''
          ? null : form.opening_height_meters,
        movement_timeout_seconds: form.movement_timeout_seconds === ''
          ? null : form.movement_timeout_seconds,
        has_built_in_auto_close: form.has_built_in_auto_close,
        is_continuous_with_leveler: form.is_continuous_with_leveler,
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשמירת השער');
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <form className="modal" onClick={e => e.stopPropagation()} onSubmit={handleSubmit}>
        <h2>עריכת שער</h2>
        <div className="form-group">
          <label>שם השער</label>
          <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
        </div>
        <div className="form-group">
          <label>מוצר מהקטלוג</label>
          <select value={form.product_id} onChange={e => handlePickProduct(e.target.value)}>
            <option value="">-- ללא (טקסט חופשי בלבד) --</option>
            {products.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}{p.model_number ? ` (${p.model_number})` : ''}{p.category_name ? ` · ${p.category_name}` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>דגם (טקסט חופשי)</label>
          <input value={form.model} onChange={e => setForm({...form, model: e.target.value})} />
        </div>
        <div className="form-group">
          <label>מזהה רכיב</label>
          <input value={form.sheli_id} onChange={e => setForm({...form, sheli_id: e.target.value})} placeholder="SHELI-XXXXXX" />
          <ShelyUrls sheliId={form.sheli_id.trim()} compact hint={false} />
        </div>
        <div className="form-group">
          <label>יעד סייקלים לטיפול</label>
          <input type="number" value={form.cycle_limit} onChange={e => setForm({...form, cycle_limit: e.target.value})} placeholder="10000" />
        </div>
        <div className="form-group">
          <label title="גובה הפתח של השער במטרים. יחד עם מהירות הדגם (במוצרים), המערכת מחשבת אוטומטית את הזמן שהיא מחכה בין פקודות. השאר ריק אם לא ידוע — נשתמש בברירת מחדל של 45 שניות.">
            גובה הפתח (מטרים)
          </label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={form.opening_height_meters}
            onChange={e => setForm({...form, opening_height_meters: e.target.value})}
            placeholder="לדוגמה: 2.5"
          />
        </div>
        <div className="form-group">
          <label title="עקיפה ידנית של זמן ההמתנה בין פקודות. השאר ריק כדי שהמערכת תחשב אוטומטית מגובה ומהירות. ערך מספרי כאן מתעלם מהחישוב האוטומטי.">
            עקיפה ידנית — זמן תנועה (שניות)
          </label>
          <input
            type="number"
            min="5"
            max="600"
            value={form.movement_timeout_seconds}
            onChange={e => setForm({...form, movement_timeout_seconds: e.target.value})}
            placeholder="ריק = חישוב אוטומטי"
          />
        </div>
        <div className="form-group">
          <label title="האם הבקר של השער סוגר את עצמו אוטומטית אחרי כמה זמן, בלי שאנחנו שולחים פקודה. כשמופעל, המערכת תימנע משליחת פקודות סגירה כפולות.">
            יש סגירה אוטומטית בבקר עצמו?
          </label>
          <YesNoSelect
            value={form.has_built_in_auto_close}
            onChange={(v) => setForm({...form, has_built_in_auto_close: v})}
          />
        </div>
        <div className="form-group">
          <label title="משווה גובה הוא הרמפה שבין משאית לרצפת המחסן — רלוונטי לרציפי העמסה.">
            יש משווה גובה מתחת לדלת?
          </label>
          <YesNoSelect
            value={form.is_continuous_with_leveler}
            onChange={(v) => setForm({...form, is_continuous_with_leveler: v})}
          />
        </div>
        <div className="form-group">
          <label>תאריך טיפול אחרון</label>
          <input type="date" value={form.last_maintenance} onChange={e => setForm({...form, last_maintenance: e.target.value})} />
        </div>
        {error && <div style={{ color: '#dc2626', marginBottom: '0.75rem' }}>{error}</div>}
        <div className="modal-actions">
          <button type="submit" className="btn btn-primary">שמור</button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>ביטול</button>
        </div>
      </form>
    </div>
  );
}

function wiringTypeLabel(wiringType) {
  // Mirror of services.commands.WIRING_* constants on the backend.
  // Anything we don't recognise we show as raw — better than a blank
  // value when a future layout type ships before the frontend.
  if (wiringType === 'toggle_with_stop') return 'Toggle + נעילה הרמטית';
  if (wiringType === 'open_close_separate') return 'פתח/סגור נפרדים';
  return wiringType || '-';
}


function movementTimeoutSourceLabel(source) {
  // Mirror of the three source values returned by
  // services.gates.movement_timeout_source. Operator-facing so
  // they understand whether the number is computed, configured,
  // or just the system default.
  if (source === 'auto') return 'חישוב לפי דגם וגובה';
  if (source === 'override') return 'עקיפה ידנית';
  if (source === 'fallback') return 'ברירת מחדל — אין נתונים';
  return source || '—';
}


// Reusable yes/no select for the gate-detail boolean flags. Plain
// <select> instead of the toggle component used in the technician
// wizard because the admin gate-edit form is full of <input> and
// <select> rows already — a button-toggle would look out of place
// there. The wizard uses big tap buttons because it's mobile-
// first; the admin form is desktop-first.
function YesNoSelect({ value, onChange }) {
  return (
    <select
      value={value ? 'yes' : 'no'}
      onChange={(e) => onChange && onChange(e.target.value === 'yes')}
    >
      <option value="no">לא</option>
      <option value="yes">כן</option>
    </select>
  );
}


export default GateDetail;
